import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, DollarSign, Package, Users, ShoppingBag } from "lucide-react";

const EMPTY = { name: "", description: "", price: "", category: "shirts", image: "", sizes: "", featured: false, active: true };

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?, ...form}

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (p) => setEditing({ ...p, price: String(p.price), sizes: (p.sizes || []).join(", ") });

  const save = async () => {
    const body = {
      name: editing.name, description: editing.description,
      price: parseFloat(editing.price) || 0, category: editing.category,
      image: editing.image, featured: editing.featured, active: editing.active,
      sizes: editing.sizes ? editing.sizes.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    if (!body.name || !body.price) { toast.error("Name and price are required"); return; }
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Product saved");
      setEditing(null); load();
    } catch { toast.error("Could not save product"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted"); load();
  };

  const statCards = stats ? [
    { icon: DollarSign, label: "Revenue", value: `$${stats.revenue.toFixed(2)}` },
    { icon: ShoppingBag, label: "Paid Orders", value: stats.paid_orders },
    { icon: Package, label: "Products", value: stats.products },
    { icon: Users, label: "Customers", value: stats.customers },
  ] : [];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-heading text-5xl font-900 uppercase tracking-tight">Admin</h1>
        <div className="flex gap-1 border border-white/10">
          {["overview", "products", "orders"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`px-5 py-2 text-sm font-semibold uppercase tracking-wider transition-colors ${
                tab === t ? "bg-[#E63946] text-white" : "text-[#A1A1AA] hover:text-white"
              }`}>{t}</button>
          ))}
        </div>
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-overview">
          {statCards.map((s) => (
            <div key={s.label} className="bg-[#121212] border border-white/10 p-6">
              <s.icon className="text-[#C5A059] mb-3" size={22} />
              <p className="text-3xl font-heading font-800">{s.value}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#52525B] mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "products" && (
        <div data-testid="admin-products">
          <button onClick={openNew} data-testid="admin-new-product"
            className="mb-6 bg-[#E63946] hover:bg-[#FF4D5A] text-white font-semibold uppercase tracking-wider text-sm px-6 py-3 flex items-center gap-2 transition-colors">
            <Plus size={16} /> New Product
          </button>
          <div className="border border-white/10">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 border-b border-white/10 p-4 bg-[#121212]">
                <img src={p.image} alt={p.name} className="w-14 h-14 object-cover bg-[#1A1A1A]" />
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-lg font-700 uppercase tracking-tight truncate">{p.name}</p>
                  <p className="text-xs text-[#A1A1AA]">{p.category} · ${p.price.toFixed(2)}{p.featured ? " · Featured" : ""}</p>
                </div>
                <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="text-[#A1A1AA] hover:text-white transition-colors"><Pencil size={18} /></button>
                <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="text-[#A1A1AA] hover:text-[#E63946] transition-colors"><Trash2 size={18} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="border border-white/10 overflow-x-auto" data-testid="admin-orders">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#52525B] uppercase text-xs tracking-wider bg-[#121212]">
                <th className="p-3">Customer</th><th className="p-3">Items</th><th className="p-3">Total</th>
                <th className="p-3">Method</th><th className="p-3">Status</th><th className="p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-white/10">
                  <td className="p-3">{o.customer_name || "Guest"}<br /><span className="text-[#52525B] text-xs">{o.customer_email}</span></td>
                  <td className="p-3 text-[#A1A1AA]">{o.items?.reduce((s, i) => s + i.quantity, 0)}</td>
                  <td className="p-3">${o.total?.toFixed(2)}</td>
                  <td className="p-3 uppercase text-xs">{o.payment_method}</td>
                  <td className="p-3">
                    <span className={`text-xs font-bold uppercase px-2 py-1 ${o.payment_status === "paid" ? "bg-[#C5A059] text-black" : "bg-white/10 text-[#A1A1AA]"}`}>
                      {o.payment_status}
                    </span>
                  </td>
                  <td className="p-3 text-[#52525B] text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#52525B]">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Product editor */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#121212] border border-white/10 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="product-editor">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-2xl font-800 uppercase tracking-tight">{editing.id ? "Edit" : "New"} Product</h3>
              <button onClick={() => setEditing(null)} className="text-[#A1A1AA] hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Name" data-testid="pe-name"
                className="w-full bg-[#050505] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#E63946] transition-colors" />
              <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Description" rows="3"
                className="w-full bg-[#050505] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#E63946] transition-colors" />
              <div className="grid grid-cols-2 gap-3">
                <input value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} placeholder="Price" type="number" step="0.01" data-testid="pe-price"
                  className="bg-[#050505] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#E63946] transition-colors" />
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="pe-category"
                  className="bg-[#050505] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#E63946] transition-colors">
                  <option value="shirts">Shirts</option>
                  <option value="stickers">Stickers</option>
                </select>
              </div>
              <input value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="Image URL" data-testid="pe-image"
                className="w-full bg-[#050505] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#E63946] transition-colors" />
              <input value={editing.sizes} onChange={(e) => setEditing({ ...editing, sizes: e.target.value })} placeholder="Sizes (comma separated, e.g. S, M, L)"
                className="w-full bg-[#050505] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#E63946] transition-colors" />
              <label className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured
              </label>
            </div>
            <button onClick={save} data-testid="pe-save"
              className="mt-6 w-full bg-[#E63946] hover:bg-[#FF4D5A] text-white font-semibold uppercase tracking-wider text-sm px-6 py-3 transition-colors">
              Save Product
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
